import { useState, useEffect, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import "./dock.css";
import { dockOutApi, getDockStatusApi, getDockViewApi, getEnabledFieldApi, uploadDockAttachmentApi } from "./dock-api";
import driverImg from "../../assets/svg/driver.svg";
import close from "../../assets/svg/close.svg";
import truck from "../../assets/svg/truck.svg";
import img from "../../assets/svg/img.svg";
import upload from "../../assets/svg/upload.svg";
import CapturePictureModal from "../common/capture-picture";
import takePhoto from "../../assets/svg/camera-add.svg";
import SearchWithAPI from "../searchbox/searchbox.js";

const DockOutModal = ({ dockId, onProceed, onCancel }) => {
   const apiUrl = process.env.REACT_APP_API_URL;
  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      opacity: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1050,
      pointerEvents: "auto",
    },
    modal: {
      backgroundColor: "#ffffff",
      padding: "14px",
      borderRadius: "16px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      width: "561px",
      height: "auto",
      position: "relative",
      zIndex: 1051,
    },
  };

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const delayReasonRef = useRef(null);

  useEffect(() => {
    getDock();
  }, [dockId]);

  useEffect(() => {
    getEnabledField();
  }, []);

  const [isDelayReasonRequired, setIsDelayReasonRequired] = useState(false);
  const [selectedOption, setSelectedOption] = useState("yes");

  const [delayReasonOption, setDelayReasonOption] = useState(null);
  const [inCompleteReasonOption, setInCompleteReasonOption] = useState(null);
  const [validations, setValidations] = useState({
    task_completed_status: "",
    task_incomplete_reason: "",
    reason: "",
  });
  const [attachmentError, setAttachmentError] = useState("");

  const handleDelayReasonDropdownChange = (option) => {
    setDelayReasonOption(option);
  };

  const handleInCompleteReasonDropdownChange = (option) => {
    setInCompleteReasonOption(option);
  };

  const [dockDetails, setDockDetails] = useState([]);
  const getDock = async () => {
    try {
      const response = await getDockViewApi(`${dockId}`);
      if (response.data.success === true) {
        getEnabledField(response.data.dock?.department_id);
        setDockDetails(response.data.dock);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching dock details:", err);
    }
  };

  const [enabledFields, setEnabledFields] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState([]);

  const getEnabledField = async (department_id) => {
    const data = {
      department_id: department_id ?? "",
      dock_id: "",
    };
    try {
      const response = await getEnabledFieldApi(data);
      if (response.success === true) {
        setEnabledFields(response?.enabled_fields ?? []);
        setMandatoryFields(response?.mandatory_fields ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching enabled fields:", err);
    }
  };

  const isFieldEnabled = (field) => {
    return enabledFields.some((item) => item === field);
  };

  const isFieldMandatory = (field) => {
    return mandatoryFields.some((item) => item === field);
  };

  const fileInputRef = useRef(null);
  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error("File input is not initialized.");
    }
  };

  const [base64FileList, setBase64FileList] = useState([]);
  const [urlImageList, setUrlImageList] = useState([]);
  const [removedImageList, setRemovedImageList] = useState([]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      if (base64FileList.length >= 5) {
        setAttachmentError("Maximum 5 attachments allowed.");
        event.target.value = "";
        return;
      }
      setAttachmentError("");
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        setBase64FileList((prev) => [...prev, base64String]);
      };
      reader.readAsDataURL(file);
    } else if (file) {
      setAttachmentError("Please select a valid image file.");
    }
    event.target.value = "";
  };

  const getDockStatus = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await getDockStatusApi(dockId);
      if (response?.status === 200 && response?.data?.success === true) {
        const dockStatus = response.data.dock?.dock_status;
        if ((dockStatus === "Over Delay" || dockStatus === "Delay") && delayReasonOption === null) {
          setIsDelayReasonRequired(true);
          setIsSubmitting(false);
          setTimeout(() => {
            if (delayReasonRef.current) {
              delayReasonRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 100);
          return;
        }
        await dockOut();
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error getting dock status:", err);
      setIsSubmitting(false);
    }
  };

  const dockOut = async () => {
    try {
      const payload = {
        task_completed_status: selectedOption === "no" ? false : true,
        task_incomplete_reason: inCompleteReasonOption != null ? inCompleteReasonOption.value : "",
        reason: delayReasonOption != null ? delayReasonOption.value : "",
      };
      const response = await dockOutApi(dockId, payload);
      if (response?.status === 200 && response?.data?.success === true) {
        if (base64FileList.length > 0) {
          try {
            await uploadDockAttachmentApi({ id: response.data.id || dockId, type: "out", attachments: base64FileList });
          } catch (uploadErr) {
            console.error("Error uploading attachments:", uploadErr);
          }
        }
        if (onProceed) {
          onProceed(response.data);
        }
      } else if (response?.status === 400 && response?.data?.error) {
        const validationTemp = {
          task_completed_status: "",
          task_incomplete_reason: "",
          reason: "",
        };
        Object.entries(response.data.error).forEach(([key, value]) => {
          validationTemp[key] = Array.isArray(value) ? (value[0] || "") : value;
        });
        setValidations(validationTemp);
      }
    } catch (err) {
      console.error("Error during dock out:", err);
      if (err.response?.status === 400 && err.response?.data?.error) {
        const validationTemp = {
          task_completed_status: "",
          task_incomplete_reason: "",
          reason: "",
        };
        Object.entries(err.response.data.error).forEach(([key, value]) => {
          validationTemp[key] = Array.isArray(value) ? (value[0] || "") : value;
        });
        setValidations(validationTemp);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSuccessCapture = (captureImage) => {
    setIsCameraOpen(false);
    if (base64FileList.length >= 5) {
      setAttachmentError("Maximum 5 attachments allowed.");
      return;
    }
    setAttachmentError("");
    setBase64FileList((prev) => [...prev, captureImage]);
  };

  return (
    <div style={modalStyle.overlay}>
      {isCameraOpen && <CapturePictureModal onSuccess={onSuccessCapture} onCancel={() => setIsCameraOpen(false)} />}
      <div style={modalStyle.modal}>
        <div style={{ color: "#ffffff" }}>
          <div className="dock-out-title style-justify-center">Confirm Dock out?</div>
          <div className="dock-out-sub-title style-justify-center">Are you sure want to dock out this vehicle right now?</div>
          <div className="dock-out-detail-box mt-2">
            <div className="style-justify-start">
              <div key={"base-image-"} className="mt-3" style={{ position: "relative", width: "72px", height: "72px" }}>
                <img onClick={handleFileClick} src={truck} alt="profile" style={{ cursor: "pointer", width: "72px", height: "72px", borderRadius: "100px", background: "#FFFFFF", padding: "10px" }} />
              </div>
              <div className="ps-4">
                <div className="dock-out-vehicle-no">{dockDetails.vehicle_number ?? ""}</div>
                <div className="dock-out-person">
                  <img src={driverImg} alt="person" />
                  <span className="ps-2 pt-2">{dockDetails.driver_name ?? ""}</span>
                </div>
              </div>
            </div>
            <div className="mt-2 d-flex" style={{ gap: "12px" }}>
              <div className="dockout-detail-box">
                <div className="dockout-title">Dock in time</div>
                <div className="dockout-title-value">{dockDetails.dock_in_time ?? ""}</div>
              </div>
              <div className="dockout-detail-box">
                <div className="dockout-title">Duration</div>
                <div className="style-justify-center">
                  <div className="dockout-title-value">{dockDetails.duration ?? "--"}</div>
                  <div className="ms-2 dockout-status">{dockDetails.dock_status ?? "--"}</div>
                </div>
              </div>
              <div className="dockout-detail-box">
                <div className="dockout-title">Lot Number</div>
                <div className="dockout-title-value">{dockDetails.display_name ?? "--"}</div>
              </div>
            </div>
          </div>
          <div>
            <div style={{ borderRadius: "12px", padding: "20px", textAlign: "center", marginBottom: "20px", backgroundColor: "#F9F9F9", display: "flex", flexDirection: "column", alignItems: "center", marginTop: "8px" }}>
              <div className="style-justify-center" style={{ gap: "8px" }}>
                <div>
                  <img src={img} alt="Upload icon" style={{ width: "40px", marginBottom: "10px" }} />
                  <div style={{ fontSize: "14px", fontWeight: 500, color: "#888" }}>Upload proof image</div>
                </div>
                <button type="button" onClick={handleFileClick} style={{ marginTop: "10px", color: "#d33", border: "1px solid #E5E5E5", padding: "10px 16px", borderRadius: "37px", background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontFamily: "epilogue", fontWeight: "500" }}>
                  <img src={upload} alt="Upload icon" style={{ width: "16px", height: "16px" }} /> Upload
                </button>
                <button type="button" onClick={() => setIsCameraOpen(true)} style={{ marginTop: "10px", color: "#d33", border: "1px solid #E5E5E5", padding: "10px 16px", borderRadius: "37px", background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontFamily: "epilogue", fontWeight: "500" }}>
                  <img src={takePhoto} alt="Capture" style={{ width: "16px", height: "16px" }} /> Take Photo
                </button>
              </div>
              {attachmentError && <span className="field-error mt-2">{attachmentError}</span>}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <div className="d-flex flex-wrap justify-content-start align-items-center gap-2">
              {urlImageList.map((urlImage, index) => (
                <div key={"url-image-" + index} className="mt-3" style={{ position: "relative" }}>
                  <img src={urlImage != null && urlImage !== "" ? urlImage.image : ""} alt="complaint" style={{ width: "72px", height: "72px", borderRadius: "14px" }} />
                  <div
                    style={{
                      position: "absolute",
                      top: "1px",
                      right: "1px",
                    }}>
                    <div style={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
                      <img
                        src={close}
                        alt=""
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const tempRemoved = [...removedImageList];
                          tempRemoved.push(urlImage.id);
                          setRemovedImageList(tempRemoved);
                          const tempURLImageList = [...urlImageList];
                          tempURLImageList.splice(index, 1);
                          setUrlImageList(tempURLImageList);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {base64FileList.map((baseImage, index) => (
                <div key={"base-image-" + index} className="mt-3" style={{ position: "relative" }}>
                  <img src={baseImage != null && baseImage !== "" ? baseImage : ""} alt="complaint" style={{ width: "72px", height: "72px", borderRadius: "14px" }} />
                  <div
                    style={{
                      position: "absolute",
                      top: "1px",
                      right: "1px",
                    }}>
                    <div style={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
                      <img
                        src={close}
                        alt=""
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const tempImageList = [...base64FileList];
                          tempImageList.splice(index, 1);
                          setBase64FileList(tempImageList);
                          if (tempImageList.length < 5) {
                            setAttachmentError("");
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {isDelayReasonRequired === true && (
            <div ref={delayReasonRef} className="mt-3 mb-3">
              <div className="field-title mb-1">Dock Out Delay Reason</div>
              <SearchWithAPI onDropdownChange={handleDelayReasonDropdownChange} apiUrl={apiUrl + "get_reasons?"} params={{ type: "dock_out_delay" }} placeholder={"Select"} selectedOption={delayReasonOption} />
              <span className="field-error">{validations.reason}</span>
            </div>
          )}

          <Row>
            {isFieldEnabled("Loading completed") && (
              <Col sm={12} lg={4}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ fontWeight: "500", fontSize: "13px", color: "#172b4d" }}>Loading completed {isFieldMandatory("Loading completed") && <span style={{ color: "#FF4444" }}>*</span>}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* Yes Option */}
                    <div onClick={() => setSelectedOption("yes")} style={{ display: "flex", alignItems: "center", gap: "8px", height: "36px", borderRadius: "12px", border: "1px solid #ebebeb", padding: "0px 13px", backgroundColor: selectedOption === "yes" ? "#FFF4F4" : "#ffffff", cursor: "pointer" }}>
                      <input type="radio" name="group" checked={selectedOption === "yes"} onChange={() => setSelectedOption("yes")} style={{ accentColor: "#ff4444" }} />
                      <div style={{ fontWeight: "600", fontSize: "16px", color: selectedOption === "yes" ? "#ff4444" : "#172b4d" }}>Yes</div>
                    </div>

                    {/* No Option */}
                    <div onClick={() => setSelectedOption("no")} style={{ display: "flex", alignItems: "center", gap: "8px", height: "36px", borderRadius: "12px", border: "1px solid #ebebeb", padding: "0px 13px", backgroundColor: selectedOption === "no" ? "#FFF4F4" : "#ffffff", cursor: "pointer" }}>
                      <input type="radio" name="group" checked={selectedOption === "no"} onChange={() => setSelectedOption("no")} style={{ accentColor: "#ff4444" }} />
                      <div style={{ fontWeight: "600", fontSize: "16px", color: selectedOption === "no" ? "#ff4444" : "#172b4d" }}>No</div>
                    </div>
                  </div>
                  <span className="field-error">{validations.task_completed_status}</span>
                </div>
              </Col>
            )}

            {isFieldEnabled("Load incomplete reason") && selectedOption === "no" && (
              <Col sm={12} lg={8} style={{ display: "flex" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                  <div style={{ fontWeight: "500", fontSize: "13px", color: "#172b4d" }}>Load incomplete reason{isFieldMandatory("Load incomplete reason") && <span style={{ color: "#FF4444" }}>*</span>}</div>

                  <div style={{ width: "100%" }}>
                    <SearchWithAPI onDropdownChange={handleInCompleteReasonDropdownChange} apiUrl={apiUrl + "get_reasons?"} params={{ type: "dock_task_incomplete" }} placeholder={"Select"} selectedOption={inCompleteReasonOption} />
                  </div>
                  <span className="field-error">{validations.task_incomplete_reason}</span>
                </div>
              </Col>
            )}
          </Row>

          <div className="style-justify-center mt-3" style={{ gap: "8px" }}>
            <button
              type="button"
              onClick={getDockStatus}
              className="dockout-confirm"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="d-flex align-items-center gap-2">
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span>Confirming...</span>
                </div>
              ) : (
                "Yes, Confirm"
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="dockout-cancel"
              disabled={isSubmitting}
            >
              No, Don’t
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DockOutModal;
